import React from 'react';

/**
 * Enhanced loading state component with different variants and better UX
 */
const EnhancedLoadingState = ({ 
  variant = 'spinner',
  size = 'medium',
  message = null,
  progress = null,
  className='',
  overlay = false,
  retryCount = 0,
  isRetrying = false
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-4 h-4';
      case 'large':
        return 'w-12 h-12';
      case 'extra-large':
        return 'w-16 h-16';
      default:
        return 'w-8 h-8';
    }
  };

  const getSpinner = () => (
    <div className={`animate-spin rounded-full border-2 border-gray-300 border-t-teal-500 dark:border-gray-600 dark:border-t-teal-400 ${getSizeClasses()}`}></div>
  );

  const getPulse = () => (
    <div className={`animate-pulse bg-teal-500 dark:bg-teal-400 rounded-full ${getSizeClasses()}`}></div>
  );

  const getDots = () => (
    <div className="flex space-x-1">
      <div className="w-2 h-2 bg-teal-500 dark:bg-teal-400 rounded-full animate-bounce"></div>
      <div className="w-2 h-2 bg-teal-500 dark:bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
      <div className="w-2 h-2 bg-teal-500 dark:bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
    </div>
  );

  const getBars = () => (
    <div className="flex space-x-1">
      <div className="w-1 h-6 bg-teal-500 dark:bg-teal-400 animate-pulse"></div>
      <div className="w-1 h-6 bg-teal-500 dark:bg-teal-400 animate-pulse" style={{ animationDelay: '0.1s' }}></div>
      <div className="w-1 h-6 bg-teal-500 dark:bg-teal-400 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
      <div className="w-1 h-6 bg-teal-500 dark:bg-teal-400 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
    </div>
  );

  const getSkeleton = () => (
    <div className="animate-pulse space-y-3">
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
      <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-5/6"></div>
    </div>
  );

  const getProgressBar = () => (
    <div className="w-full">
      <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-1 dark:text-gray-300 dark:text-gray-600">
        <span>{message || 'Loading...'}</span>
        {progress !== null && <span>{Math.round(progress)}%</span>}
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div 
          className="bg-teal-500 dark:bg-teal-400 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: progress !== null ? `${progress}%` : '30%' }}
        ></div>
      </div>
    </div>
  );

  const getLoadingIndicator = () => {
    switch (variant) {
      case 'pulse':
        return getPulse();
      case 'dots':
        return getDots();
      case 'bars':
        return getBars();
      case 'skeleton':
        return getSkeleton();
      case 'progress':
        return getProgressBar();
      default:
        return getSpinner();
    }
  };

  const content = (
    <div className={`flex flex-col items-center justify-center space-y-3 ${className}`}>
      {variant !== 'progress' && variant !== 'skeleton' && getLoadingIndicator()}
      {variant === 'progress' && getProgressBar()}
      {variant === 'skeleton' && getSkeleton()}
      
      {message && variant !== 'progress' && (
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
            {isRetrying ? `Retrying... (Attempt ${retryCount + 1})` : message}
          </p>
          {isRetrying && retryCount > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Previous attempts failed, trying again...
            </p>
          )}
        </div>
      )}
    </div>
  );

  if (overlay) {
    return (
      <div className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-xl">
          {content}
        </div>
      </div>
    );
  }

  return content;
};

/**
 * Loading skeleton for specific content types
 */
export const LoadingSkeleton = ({ type = 'text', count = 3, className='' }) => {
  const getSkeletonForType = () => {
    switch (type) {
      case 'card':
        return (
          <div className="animate-pulse space-y-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
            <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-5/6"></div>
          </div>
        );
      
      case 'list':
        return (
          <div className="animate-pulse space-y-2">
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                <div className="flex-1 space-y-1">
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-3/4"></div>
                  <div className="h-2 bg-gray-300 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        );
      
      case 'table':
        return (
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded mb-4"></div>
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="flex space-x-4 mb-2">
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded flex-1"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded flex-1"></div>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded flex-1"></div>
              </div>
            ))}
          </div>
        );
      
      case 'form':
        return (
          <div className="animate-pulse space-y-4">
            {Array.from({ length: count }).map((_, index) => (
              <div key={index}>
                <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/4 mb-2"></div>
                <div className="h-10 bg-gray-300 dark:bg-gray-600 rounded"></div>
              </div>
            ))}
          </div>
        );
      
      default:
        return (
          <div className="animate-pulse space-y-2">
            {Array.from({ length: count }).map((_, index) => (
              <div key={index} className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-full"></div>
            ))}
          </div>
        );
    }
  };

  return (
    <div className={className}>
      {getSkeletonForType()}
    </div>
  );
};

/**
 * Inline loading indicator for buttons and small spaces
 */
export const InlineLoader = ({ size = 'small', className='' }) => {
  return (
    <div className={`inline-flex items-center ${className}`}>
      <div className={`animate-spin rounded-full border-2 border-gray-300 border-t-current ${
        size === 'small' ? 'w-3 h-3' : 'w-4 h-4'
      }`}></div>
    </div>
  );
};

/**
 * Loading overlay for specific elements
 */
export const LoadingOverlay = ({ loading, children, message = 'Loading...', className='' }) => {
  return (
    <div className={`relative ${className}`}>
      {children}
      {loading && (
        <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm flex items-center justify-center rounded-lg dark:bg-gray-800">
          <EnhancedLoadingState message={message} />
        </div>
      )}
    </div>
  );
};

export default EnhancedLoadingState;
